import { zodResolver } from '@hookform/resolvers/zod';
import { Search } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { FormGroup } from '@/components/FormGroup';
import { Input } from '@/components/ui/Input';

const schema = z.object({
  search: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

export interface ModelsFilterValue {
  promptName?: string;
  serviceName?: string;
}

function parseSearch(value?: string): ModelsFilterValue {
  const q = (value ?? '').trim();
  if (!q) return {};
  const idx = q.indexOf(':');
  if (idx !== -1) {
    const left = q.slice(0, idx).trim();
    const right = q.slice(idx + 1).trim();
    return {
      promptName: left || undefined,
      serviceName: right || undefined,
    };
  }
  return { promptName: q };
}

export function ModelsFilter({
  onFilterChange,
  filter,
}: {
  onFilterChange: (v: ModelsFilterValue) => void;
  filter: ModelsFilterValue;
}) {
  const { register, watch, setValue } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { search: '' },
  });

  useEffect(() => {
    // initialize search input from filter values
    if (filter.promptName || filter.serviceName) {
      const composed = filter.serviceName
        ? `${filter.promptName ?? ''}:${filter.serviceName}`
        : (filter.promptName ?? '');
      setValue('search', composed);
    }
  }, [filter.promptName, filter.serviceName, setValue]);

  const search = watch('search');

  useEffect(() => {
    onFilterChange(parseSearch(search));
  }, [search, onFilterChange]);

  return (
    <div className="relative flex-1">
      <FormGroup>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Filter models by prompt or service (use prompt:service)"
            className="pl-10"
            {...register('search')}
          />
        </div>
        <div className="px-1 text-xs text-muted-foreground">
          Tip: combine filters like &apos;prompt:service&apos;. Example:
          homepage_banner:TONE_OF_VOICE
        </div>
      </FormGroup>
    </div>
  );
}
